// supervisor-outcome-router-worker-outcome.ts
//
// SOL-02 (audit Sol 2026-09-28, H2): the worker's OWN `result.outcome`.
// result-verifier.ts judges the artifacts (scope, evidence, recipes) and never
// reads `outcome`, so a verdict of "done" only means "nothing the worker left
// behind is wrong" — not "the worker finished". A worker that honestly said
// `failed`/`blocked`/`needs_decision` used to be committed and marked done.
// supervisor-outcome-router.ts consults these helpers BEFORE its done path.
//
// Split out of the router to keep it under the 200-line budget, together with
// attemptOutcomeFromCycle (moved verbatim + the SOL-02 evidence prefix).

import type { AutopilotResult } from "./autopilot-result-schema.js";
import type { AttemptOutcome, RetryDecision, RetryState } from "./retry-policy.js";
import { redactKnownValues } from "./secret-redactor.js";
import { appendStderrTailToEvidence } from "./session-failure-reasons.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";

export type WorkerDeclaredOutcome = "done" | "failed" | "blocked" | "needs_decision";

const WORKER_OUTCOMES: readonly string[] = ["done", "failed", "blocked", "needs_decision"];
/** Journal/evidence budget for the worker's free-text summary. */
const SUMMARY_MAX_CHARS = 500;

interface DeclaredFields {
  outcome?: unknown;
  error_class?: unknown;
  summary?: unknown;
}

function declaredFields(cycle: AttemptCycleResult): DeclaredFields | undefined {
  if (cycle.kind !== "verified") return undefined;
  const result = cycle.sessionResult.result;
  return typeof result === "object" && result !== null ? (result as DeclaredFields) : undefined;
}

/** `result.outcome` of a verified cycle; `undefined` when absent (contract_error, no result). */
export function workerDeclaredOutcome(
  cycle: AttemptCycleResult,
): WorkerDeclaredOutcome | undefined {
  const outcome = declaredFields(cycle)?.outcome;
  return typeof outcome === "string" && WORKER_OUTCOMES.includes(outcome)
    ? (outcome as WorkerDeclaredOutcome)
    : undefined;
}

/** "worker khai outcome=<o> (<error_class>): <summary>" for failed/blocked; else undefined. */
export function workerOutcomeEvidence(
  cycle: AttemptCycleResult,
  secretValues: readonly string[] = [],
): string | undefined {
  const outcome = workerDeclaredOutcome(cycle);
  if (outcome !== "failed" && outcome !== "blocked") return undefined;
  const fields = declaredFields(cycle) ?? {};
  const errorClass = typeof fields.error_class === "string" ? ` (${fields.error_class})` : "";
  // Review 04a L1: che (known-values, tầng hai sau parse) TRƯỚC khi cắt — cắt
  // trước sẽ để lại nửa đầu một secret vắt qua điểm cắt.
  const summary =
    typeof fields.summary === "string"
      ? redactKnownValues(fields.summary, secretValues).slice(0, SUMMARY_MAX_CHARS)
      : "";
  return `worker khai outcome=${outcome}${errorClass}: ${summary}`;
}

/** SOL-02: verifier verdict done AND the worker itself declared done. */
export function isDoneOutcome(cycle: AttemptCycleResult): boolean {
  return (
    cycle.kind === "verified" &&
    cycle.verdict.kind === "done" &&
    workerDeclaredOutcome(cycle) === "done"
  );
}

/** Retry-policy input for every non-done, non-quota, non-needs_decision attempt. */
export function attemptOutcomeFromCycle(
  cycle: AttemptCycleResult,
  secretValues: readonly string[] = [],
): AttemptOutcome {
  if (cycle.kind === "contract_error") {
    return { category: "contract_error", evidence: cycle.reason };
  }
  const category = cycle.sessionResult.category ?? "task_failed";
  // SOL-02: a worker-declared `failed` leads the evidence — with a "done"
  // verdict there are no verifier reasons at all to explain the retry.
  const base = cycle.sessionResult.reason ?? cycle.verdict.reasons.join("; ");
  const declared = workerOutcomeEvidence(cycle, secretValues);
  // Phase 04 (F1b, Req 1/3): append worker stderr to evidence only — category/
  // isTimeout stay untouched (Req 3). stderrTail is only ever set non-"ok".
  const evidence = appendStderrTailToEvidence(
    declared === undefined ? base : [declared, base].filter(Boolean).join("; "),
    cycle.sessionResult.outcome !== "ok" ? cycle.sessionResult.stderrTail : undefined,
    secretValues,
  );
  return { category, isTimeout: cycle.sessionResult.outcome === "deadline_exceeded", evidence };
}

/**
 * `blocked` ⇒ ticket blocked NOW, no retry: pack workflows/autopilot/
 * escalation.md — permission/resource_missing "Blocked với capability/tên
 * key/đối tượng cần bổ sung. Không thử cách lách policy". Re-running the same
 * ticket cannot conjure the missing resource. Counts as a physical run, not
 * as a counted failure (the worker was not wrong to stop).
 */
export function blockedByWorkerDecision(
  state: RetryState,
  cycle: AttemptCycleResult,
  secretValues: readonly string[] = [],
): RetryDecision {
  // Phase 04a: summary của worker (sau JSON.parse) che known-values — tầng hai.
  const evidence = workerOutcomeEvidence(cycle, secretValues) ?? "worker khai outcome=blocked";
  return {
    outcome: "blocked",
    state: { ...state, physical_runs: state.physical_runs + 1 },
    reason: evidence,
    evidence,
    category: "task_failed",
  };
}

/** `sessionResult.result` is already `AutopilotResultSchema`-validated JSON by the time it reaches here. */
export function extractProposedChanges(
  cycle: AttemptCycleResult,
): AutopilotResult["proposed_changes"] {
  if (cycle.kind !== "verified") return [];
  const result = cycle.sessionResult.result as Partial<AutopilotResult> | undefined;
  return Array.isArray(result?.proposed_changes) ? result.proposed_changes : [];
}

/** F-1: `proposed_tickets` is never applied — the done path rejects it when non-empty. */
export function extractProposedTickets(cycle: AttemptCycleResult): readonly unknown[] {
  if (cycle.kind !== "verified") return [];
  const result = cycle.sessionResult.result as Partial<AutopilotResult> | undefined;
  return Array.isArray(result?.proposed_tickets) ? result.proposed_tickets : [];
}

/**
 * 06h R3: the operator interrupted the run (Ctrl-C/SIGTERM/SIGHUP) and the
 * worker died without printing a result (`missing_result_event`) — a real run
 * (physical_runs +1, so the next attempt's log/forensics dir never collides)
 * but not the worker's fault: counted/timeout/infra counters untouched.
 * Review 06h M2: `contract_error` also covers a result the worker DID print
 * but which failed the schema (`invalid_structured_output_schema`,
 * `no_result_json_found`) — those stay a counted failure even under a signal.
 */
export function isInterruptedWithoutResult(
  cycle: AttemptCycleResult,
  signal: NodeJS.Signals | undefined,
): signal is NodeJS.Signals {
  return (
    signal !== undefined &&
    cycle.kind === "verified" &&
    cycle.sessionResult.outcome === "contract_error" &&
    cycle.sessionResult.reason === "missing_result_event"
  );
}

export function interruptedAttemptDecision(
  state: RetryState,
  signal: NodeJS.Signals,
  evidence: string | undefined,
): RetryDecision {
  const reason = `bị ngắt bởi ${signal} của người vận hành — không tính counted_failures`;
  return {
    outcome: "retry",
    state: { ...state, physical_runs: state.physical_runs + 1 },
    reason,
    evidence: evidence === undefined ? reason : `${reason}; ${evidence}`,
    category: "interrupted",
  };
}
